import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import {
  AdminApiError,
  updateAdminDepartment,
  type DepartmentAdminResponse,
  type DepartmentUpdatePayload,
} from "../../api/admin";
import "./DepartmentEditPanel.css";

export type DepartmentEditPanelProps = {
  department: DepartmentAdminResponse;
  disabled: boolean;
  onUpdated: () => Promise<void>;
  onCancel: () => void;
};

type FieldErrors = {
  name: string;
  sortOrder: string;
  routing: string;
};

type FormState = {
  name: string;
  sortOrder: string;
  isRoutingEnabled: boolean;
};

type UiState = {
  isSubmitting: boolean;
  fieldErrors: FieldErrors;
  formError: string;
  success: string;
  noChange: string;
};

const EMPTY_FIELD_ERRORS: FieldErrors = {
  name: "",
  sortOrder: "",
  routing: "",
};

function initialUi(): UiState {
  return {
    isSubmitting: false,
    fieldErrors: { ...EMPTY_FIELD_ERRORS },
    formError: "",
    success: "",
    noChange: "",
  };
}

function buildFormFromDepartment(department: DepartmentAdminResponse): FormState {
  return {
    name: department.name,
    sortOrder: String(department.sort_order),
    isRoutingEnabled: department.is_ticket_routing_enabled,
  };
}

function resolveFieldNameFromLocation(
  location: ReadonlyArray<string | number>,
): string | null {
  for (let index = location.length - 1; index >= 0; index -= 1) {
    const segment = location[index];
    if (typeof segment === "string" && segment.length > 0) {
      return segment;
    }
  }
  return null;
}

export default function DepartmentEditPanel(props: DepartmentEditPanelProps) {
  const { department, disabled, onUpdated, onCancel } = props;
  const [form, setForm] = useState<FormState>(() => buildFormFromDepartment(department));
  const [ui, setUi] = useState<UiState>(initialUi);
  const headingId = useId();
  const nameErrorId = useId();
  const sortErrorId = useId();
  const routingErrorId = useId();
  const formErrorId = useId();
  const successId = useId();
  const noChangeId = useId();
  const companyContextId = useId();
  const codeContextId = useId();
  const statusContextId = useId();
  const statusHintId = useId();
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const isMountedRef = useRef(true);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, [department.id]);

  const clearNotifications = useCallback(() => {
    setUi((prev) => ({
      ...prev,
      fieldErrors: { ...EMPTY_FIELD_ERRORS },
      formError: "",
      success: "",
      noChange: "",
    }));
  }, []);

  const buildClientFieldErrors = useCallback((): FieldErrors => {
    const trimmedName = form.name.trim();
    const errors: FieldErrors = { ...EMPTY_FIELD_ERRORS };
    if (trimmedName.length === 0) {
      errors.name = "Department name is required.";
    }
    const trimmedSort = form.sortOrder.trim();
    const sortAsNumber = Number(trimmedSort);
    if (trimmedSort.length === 0) {
      errors.sortOrder = "Sort order is required.";
    } else if (
      !Number.isFinite(sortAsNumber) ||
      !Number.isInteger(sortAsNumber)
    ) {
      errors.sortOrder = "Sort order must be a whole number.";
    }
    return errors;
  }, [form.name, form.sortOrder]);

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setForm((prev) => ({ ...prev, name: nextValue }));
      setUi((prev) => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, name: "" },
        formError: "",
        success: "",
        noChange: "",
      }));
    },
    [],
  );

  const handleSortOrderChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setForm((prev) => ({ ...prev, sortOrder: nextValue }));
      setUi((prev) => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, sortOrder: "" },
        formError: "",
        success: "",
        noChange: "",
      }));
    },
    [],
  );

  const handleRoutingToggle = useCallback(() => {
    setForm((prev) => ({ ...prev, isRoutingEnabled: !prev.isRoutingEnabled }));
    setUi((prev) => ({
      ...prev,
      fieldErrors: { ...prev.fieldErrors, routing: "" },
      formError: "",
      success: "",
      noChange: "",
    }));
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitInFlightRef.current || disabled) {
        return;
      }
      clearNotifications();

      const clientErrors = buildClientFieldErrors();
      if (
        clientErrors.name.length > 0 ||
        clientErrors.sortOrder.length > 0
      ) {
        setUi((prev) => ({
          ...prev,
          fieldErrors: clientErrors,
        }));
        return;
      }

      const trimmedName = form.name.trim();
      const trimmedSort = form.sortOrder.trim();
      const sortAsNumber = Number(trimmedSort);
      if (
        trimmedSort.length === 0 ||
        !Number.isFinite(sortAsNumber) ||
        !Number.isInteger(sortAsNumber)
      ) {
        return;
      }

      const payload: DepartmentUpdatePayload = {};
      if (trimmedName !== department.name) {
        payload.name = trimmedName;
      }
      if (sortAsNumber !== department.sort_order) {
        payload.sort_order = sortAsNumber;
      }
      if (form.isRoutingEnabled !== department.is_ticket_routing_enabled) {
        payload.is_ticket_routing_enabled = form.isRoutingEnabled;
      }

      if (
        payload.name === undefined &&
        payload.sort_order === undefined &&
        payload.is_ticket_routing_enabled === undefined
      ) {
        setUi((prev) => ({
          ...prev,
          noChange: "No changes to save.",
        }));
        return;
      }

      submitInFlightRef.current = true;
      setUi((prev) => ({ ...prev, isSubmitting: true }));

      try {
        await updateAdminDepartment(department.id, payload);
        if (!isMountedRef.current) {
          return;
        }
        setUi((prev) => ({
          ...prev,
          isSubmitting: false,
          success: "Department updated.",
          noChange: "",
        }));
        await onUpdated();
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        if (error instanceof AdminApiError) {
          const fieldErrors: FieldErrors = { ...EMPTY_FIELD_ERRORS };
          let hasRecognisedFieldError = false;
          let hasUnrecognisedFieldError = false;
          for (const fieldError of error.fieldErrors) {
            const fieldName = resolveFieldNameFromLocation(fieldError.location);
            if (fieldName === null) {
              hasUnrecognisedFieldError = true;
              continue;
            }
            if (fieldName === "name") {
              fieldErrors.name = fieldError.message;
              hasRecognisedFieldError = true;
            } else if (fieldName === "sort_order") {
              fieldErrors.sortOrder = fieldError.message;
              hasRecognisedFieldError = true;
            } else if (fieldName === "is_ticket_routing_enabled") {
              fieldErrors.routing = fieldError.message;
              hasRecognisedFieldError = true;
            } else {
              hasUnrecognisedFieldError = true;
            }
          }
          const showDetail =
            !hasRecognisedFieldError || hasUnrecognisedFieldError;
          setUi((prev) => ({
            ...prev,
            isSubmitting: false,
            fieldErrors,
            formError: showDetail ? error.detail : "",
          }));
          return;
        }
        setUi((prev) => ({
          ...prev,
          isSubmitting: false,
          formError: "Unable to update the department. Please try again.",
        }));
      } finally {
        if (isMountedRef.current) {
          submitInFlightRef.current = false;
        }
      }
    },
    [
      buildClientFieldErrors,
      clearNotifications,
      department.id,
      department.is_ticket_routing_enabled,
      department.name,
      department.sort_order,
      disabled,
      form.isRoutingEnabled,
      form.name,
      form.sortOrder,
      onUpdated,
    ],
  );

  const allInputsDisabled = disabled || ui.isSubmitting;
  const statusLabel = department.is_active ? "Active" : "Inactive";
  const statusHelpText = department.is_active
    ? "Active departments are visible in operational lists."
    : "Inactive departments are hidden from operational lists but can still be edited.";

  return (
    <section
      className="department-edit-panel"
      aria-labelledby={headingId}
      id={`department-edit-panel-${department.id}`}
    >
      <h2 id={headingId} className="department-edit-panel__title">
        Edit Department
      </h2>
      <dl className="department-edit-panel__context" aria-label="Read-only context">
        <div className="department-edit-panel__context-row">
          <dt className="department-edit-panel__context-label">Company</dt>
          <dd
            id={companyContextId}
            className="department-edit-panel__context-value"
          >
            {department.company_name ?? "Unknown company"}
          </dd>
        </div>
        <div className="department-edit-panel__context-row">
          <dt className="department-edit-panel__context-label">Department code</dt>
          <dd
            id={codeContextId}
            className="department-edit-panel__context-value"
          >
            {department.code}
          </dd>
        </div>
        <div className="department-edit-panel__context-row">
          <dt className="department-edit-panel__context-label">Status</dt>
          <dd
            id={statusContextId}
            className="department-edit-panel__context-value"
          >
            {statusLabel}
          </dd>
        </div>
      </dl>

      <form
        noValidate
        className="department-edit-panel__form"
        onSubmit={handleSubmit}
        aria-describedby={statusHintId}
      >
        <p className="department-edit-panel__hint" id={statusHintId}>
          {statusHelpText}
        </p>

        <div className="department-edit-panel__field">
          <label
            htmlFor="department-edit-name"
            className="department-edit-panel__label"
          >
            Department name
          </label>
          <input
            id="department-edit-name"
            ref={nameInputRef}
            className="department-edit-panel__input"
            type="text"
            value={form.name}
            onChange={handleNameChange}
            disabled={allInputsDisabled}
            required
            aria-required="true"
            aria-invalid={ui.fieldErrors.name.length > 0}
            aria-describedby={
              ui.fieldErrors.name.length > 0 ? nameErrorId : undefined
            }
          />
          {ui.fieldErrors.name.length > 0 ? (
            <p
              id={nameErrorId}
              className="department-edit-panel__field-error"
            >
              {ui.fieldErrors.name}
            </p>
          ) : null}
        </div>

        <div className="department-edit-panel__field">
          <label
            htmlFor="department-edit-sort-order"
            className="department-edit-panel__label"
          >
            Sort order
          </label>
          <input
            id="department-edit-sort-order"
            className="department-edit-panel__input"
            type="number"
            inputMode="numeric"
            step={1}
            value={form.sortOrder}
            onChange={handleSortOrderChange}
            disabled={allInputsDisabled}
            required
            aria-required="true"
            aria-invalid={ui.fieldErrors.sortOrder.length > 0}
            aria-describedby={
              ui.fieldErrors.sortOrder.length > 0 ? sortErrorId : undefined
            }
          />
          {ui.fieldErrors.sortOrder.length > 0 ? (
            <p
              id={sortErrorId}
              className="department-edit-panel__field-error"
            >
              {ui.fieldErrors.sortOrder}
            </p>
          ) : null}
        </div>

        <div className="department-edit-panel__field department-edit-panel__field--inline">
          <label
            htmlFor="department-edit-routing"
            className="department-edit-panel__label department-edit-panel__label--inline"
          >
            <input
              id="department-edit-routing"
              type="checkbox"
              checked={form.isRoutingEnabled}
              onChange={handleRoutingToggle}
              disabled={allInputsDisabled}
              aria-invalid={ui.fieldErrors.routing.length > 0}
              aria-describedby={
                ui.fieldErrors.routing.length > 0 ? routingErrorId : undefined
              }
            />
            <span>Ticket routing enabled</span>
          </label>
          {ui.fieldErrors.routing.length > 0 ? (
            <p
              id={routingErrorId}
              className="department-edit-panel__field-error"
            >
              {ui.fieldErrors.routing}
            </p>
          ) : null}
        </div>

        {ui.formError.length > 0 ? (
          <p
            id={formErrorId}
            className="department-edit-panel__form-error"
            role="alert"
          >
            {ui.formError}
          </p>
        ) : null}

        {ui.success.length > 0 ? (
          <p
            id={successId}
            className="department-edit-panel__success"
            aria-live="polite"
            role="status"
          >
            {ui.success}
          </p>
        ) : null}

        {ui.noChange.length > 0 ? (
          <p
            id={noChangeId}
            className="department-edit-panel__no-change"
            aria-live="polite"
            role="status"
          >
            {ui.noChange}
          </p>
        ) : null}

        <div
          className="department-edit-panel__actions"
          role="group"
          aria-labelledby={headingId}
        >
          <button
            type="submit"
            className="department-edit-panel__submit"
            disabled={allInputsDisabled}
            aria-busy={ui.isSubmitting}
          >
            {ui.isSubmitting ? "Saving…" : "Save changes"}
          </button>
          <button
            type="button"
            className="department-edit-panel__cancel"
            onClick={onCancel}
            disabled={ui.isSubmitting}
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}
